// src/api.js
import config from './config';

const API_CONTROL_URL = config.API_CONTROL_URL;
const API_FACEID_URL = config.API_FACEID_URL;


// Function to handle login
export const login = async (user, password) => {
  try {
    const response = await fetch(`${API_CONTROL_URL}/auth/v2/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    return data; // Returns the JWT token
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Function to fetch the list of accounts
export const getAccounts = async (token) => {
  try {
    const response = await fetch(`${API_CONTROL_URL}/corp/v1/accounts`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch accounts');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

// Function to fetch the list of drivers
export const getDrivers = async (token) => {
  try {
    const response = await fetch(`${API_CONTROL_URL}/drivers/v1/drivers`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch drivers');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

// Function to fetch all driver FaceId statuses (optimized - single API call)
export const getAllDriverStatuses = async (token) => {
  try {
    const url = `${API_FACEID_URL}/api/drivers/status`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch driver statuses');
    }

    const data = await response.json();
    
    // Handle new response structure: { drivers: [...], size: number }
    if (data.drivers && Array.isArray(data.drivers)) {
      return data.drivers;
    }
    
    // Fallback for old response structure (array)
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching driver statuses:', error);
    throw error;
  }
};

// Function to fetch driver FaceId status (legacy - for backward compatibility)
export const getDriverStatus = async (token, driverId) => {
  try {
    let url = `${API_FACEID_URL}/api/drivers/status`;
    if (driverId) {
      url += `?driver_id=${driverId}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch driver status');
    }

    const data = await response.json();
    
    // Handle new response structure: { drivers: [...], size: number }
    if (data.drivers && Array.isArray(data.drivers)) {
      return data.drivers;
    }
    
    // Fallback for old response structure (array)
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching driver status:', error);
    throw error;
  }
};






// Function to get the fleet-specific token
export const getFleetToken = async (orgToken, fleetUid) => {
  try {
    const response = await fetch(`${API_CONTROL_URL}/auth/v2/session/${fleetUid}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${orgToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get fleet token for fleet ${fleetUid}`);
    }

    const data = await response.json();
    return data.access_token; // Return only the fleet token
  } catch (error) {
    console.error(`Error getting fleet token for fleet ${fleetUid}:`, error);
    throw error;
  }
};

// src/api.js
// ... (previous code)

// Function to fetch driver's pictures
export const getDriverPictures = async (token, driverId) => {
  try {
    const response = await fetch(`${API_FACEID_URL}/api/pictures/${driverId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch driver pictures');
    }

    const data = await response.json();
    
    // Handle new response structure: { id, status, pictures: [...], driver_uid, created_date, last_modified_date }
    if (data.pictures && Array.isArray(data.pictures)) {
      // Transform new field names to old field names for backward compatibility
      const transformedPictures = data.pictures.map(picture => ({
        picture_id: picture.id, // Keep id as picture_id (integer)
        picture_uid: picture.id, // Keep for backward compatibility
        picture_status: picture.status, // Map status to picture_status
        is_primary: picture.is_primary,
        upload_date: picture.created_date || '2025-08-29T13:52:16.226592Z', // Map created_date to upload_date
        update_date: picture.last_modified_date || '2025-08-29T13:52:16.226592Z' // Map last_modified_date to update_date
      }));
      return transformedPictures;
    }
    
    // Fallback for old response structure (direct pictures array)
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching driver pictures:', error);
    throw error;
  }
};

// Function to delete a picture
export const deletePicture = async (token, pictureId) => {
  try {
    const response = await fetch(`${API_FACEID_URL}/api/picture/${pictureId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete picture');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting picture:', error);
    throw error;
  }
};

// Function to fetch a single image and return its URL
export const getDriverPicture = async (token, pictureId) => {
  try {
    const response = await fetch(
      `${API_FACEID_URL}/api/picture/${pictureId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch image ${pictureId}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(`Error fetching image ${pictureId}:`, error);
    throw error;
  }
};

// Function to fetch fleet details
export const getFleetDetails = async (token, fleetUid) => {
  try {
    const accounts = await getAccounts(token);
    // Filter out organizations (type 1) and find the matching fleet
    const fleet = accounts.find(account => account.type === 1 && account.uid === fleetUid);
    
    if (!fleet) {
      throw new Error('Fleet not found');
    }

    return fleet;
  } catch (error) {
    console.error('Error fetching fleet details:', error);
    throw error;
  }
};


// Update picture status via new endpoint (0: Pending, 20: Not valid, 30: Valid)
export const updatePictureStatusNew = async (token, pictureId, status) => {
  try {
    const response = await fetch(`${API_FACEID_URL}/api/picture/${pictureId}/${status}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to update picture status');
    }

    // Check if response has content to parse as JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // If no JSON content, return a success indicator
      return { success: true, status: status, picture_id: pictureId };
    }
  } catch (error) {
    console.error('Error updating picture status:', error);
    throw error;
  }
};

// Validate a picture (changes status to 30)
export const validatePicture = async (token, pictureId) => {
  return updatePictureStatusNew(token, pictureId, 30);
};

// Set primary picture
export const setPrimaryPicture = async (token, pictureId) => {
  try {
    const response = await fetch(`${API_FACEID_URL}/api/picture/${pictureId}/primary`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to set primary picture');
    }

    // Check if response has content to parse as JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // If no JSON content, return a success indicator
      return { success: true, primary_picture_uid: `pic-${pictureId}` };
    }
  } catch (error) {
    console.error('Error setting primary picture:', error);
    throw error;
  }
};

